/**
 * NotificationBell (WP 2.8 §24; TB-7 kapanışı, kapsam K-15): uygulama içi hatırlatıcı bildirimleri.
 * WP 2.9 (K-12): "Size gelenler" — alan bildirimleri (`GET /notifications`); okundu/kapat/tümünü okundu; okuma anında
 * erişimi kaybolan kaydın bildirimi sunucuda süzülür. Sayaç = tetiklenmiş hatırlatıcı + görünür okunmamış bildirim.
 * - Kaynak: `GET /reminders?status=triggered` (sunucu yalnız hâlâ erişilebilen kayıtların hatırlatıcılarını döndürür;
 *   erişim kaybolan kayıt listeden düşer — kayıt başlığı/alanı bildirimde yer almaz, yalnız kullanıcının kendi
 *   yazdığı hatırlatıcı başlığı).
 * - "Okunmamış" = `triggered` durumu; ayrı sahte okunma bayrağı YOK. Kullanıcı tamamla/ertele/kapat ile işler.
 * - Sayaç sayfa sınırıyla kesindir; sınır aşılırsa "N+" (kesin sayı gibi sunulmaz).
 * - Yoklama: 60 sn; sekme gizliyken durur, görünür olunca hemen yenilenir; istekler iptal edilebilir. WebSocket/SSE yok,
 *   harici bildirim yok.
 */
import { useEffect, useId, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlarmClock, AlertTriangle, Bell, MessageSquare, UserPlus } from 'lucide-react';
import type { Reminder } from '@castinflow/shared';
import {
  Button,
  Portal,
  Select,
  Spinner,
  anchoredStyle,
  cn,
  useAnchoredPosition,
  useDismissable,
  useToast,
} from '@castinflow/ui';
import { qk, useScope } from '../../lib/scope';
import {
  actOnReminder,
  fetchMyReminders,
  fetchNotifications,
  markAllNotificationsRead,
  markNotification,
  snoozeReminder,
} from '../api';
import { shellStrings as trf } from '../shell-strings';
import { recordHref, enabledType } from '../routes';
import { useLive } from '../live/use-live';
import {
  NOTIFICATION_CATEGORIES,
  groupNotifications,
  notificationAction,
  notificationCategory,
  notificationText,
  type NotificationCategory,
} from './notification-view';
import { formatDateTimeTr } from '../../lib/dates';

/** Hatırlatıcının kendi saat diliminde kısa tarih-saat (biçimleyici modülü kabuk paketine çekilmez). */
function formatDue(iso: string, timeZone: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  try {
    return formatDateTimeTr(d, { timeZone });
  } catch {
    return formatDateTimeTr(d);
  }
}

export const NOTIFICATION_PAGE = 20;
export const NOTIFICATION_POLL_MS = 60_000;
/** TI-013: canlı kanal açıkken yoklama yalnız güvenlik ağıdır (kanal ipucu verince hemen yenilenir) */
export const NOTIFICATION_LIVE_POLL_MS = 5 * 60_000;
/** Bildirim zamanı tarayıcının saat diliminde (kabuk metadata yüklemez) */
const localZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

function usePageVisible(): boolean {
  const [visible, setVisible] = useState(() => document.visibilityState !== 'hidden');
  useEffect(() => {
    const on = () => {
      setVisible(document.visibilityState !== 'hidden');
    };
    document.addEventListener('visibilitychange', on);
    return () => {
      document.removeEventListener('visibilitychange', on);
    };
  }, []);
  return visible;
}

/** Sayaç metni: sayfa sınırı aşıldıysa "N+". */
export function countLabel(n: number, more: boolean): string {
  return more ? `${n}+` : String(n);
}

export function NotificationBell() {
  const scope = useScope();
  const qc = useQueryClient();
  const toast = useToast();
  const visible = usePageVisible();
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const layerRef = useRef<HTMLDivElement>(null);
  const base = useId().replace(/:/g, '');
  const pos = useAnchoredPosition(open, triggerRef, layerRef, 'end');
  // TI-013: canlı kanal — yeni bildirim ipucunda (ve kopma sonrası) sayaç ile liste hemen yenilenir; kanal yoksa 60 sn yoklama
  const live = useLive((m) => {
    if (!scope || (m.type !== 'notification' && m.type !== 'resync')) return;
    void qc.invalidateQueries({ queryKey: qk.inbox(scope) });
    void qc.invalidateQueries({ queryKey: qk.notifications(scope) });
  }, scope !== null);
  const pollMs = live === 'live' ? NOTIFICATION_LIVE_POLL_MS : NOTIFICATION_POLL_MS;
  const [inboxScope, setInboxScope] = useState<'unread' | 'all'>('all');
  const [category, setCategory] = useState<NotificationCategory>('all');
  const [openGroups, setOpenGroups] = useState<string[]>([]);
  const q = useQuery({
    queryKey: scope ? qk.notifications(scope) : ['notifications', 'none'],
    queryFn: ({ signal }) => fetchMyReminders('triggered', NOTIFICATION_PAGE, signal),
    enabled: scope !== null,
    refetchInterval: visible ? pollMs : false,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    staleTime: 15_000,
  });
  // WP 2.9: uygulama içi bildirimler (ilk sayfa yoklanır; eski sayfalar yalnız istenince)
  const [inboxPages, setInboxPages] = useState<string[]>([]);
  const inbox = useQuery({
    queryKey: scope ? qk.inbox(scope) : ['inbox', 'none'],
    queryFn: ({ signal }) => fetchNotifications(null, signal),
    enabled: scope !== null,
    refetchInterval: visible ? pollMs : false,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    staleTime: 15_000,
  });
  const older = useQueries({
    queries: inboxPages.map((cursor) => ({
      queryKey: scope ? [...qk.inbox(scope), cursor] : ['inbox', 'none', cursor],
      queryFn: () => fetchNotifications(cursor),
      enabled: scope !== null && open,
    })),
  });
  const inboxItems = [...(inbox.data?.data ?? []), ...older.flatMap((r) => r.data?.data ?? [])];
  const inboxNext =
    (inboxPages.length === 0
      ? inbox.data?.nextCursor
      : older[older.length - 1]?.data?.nextCursor) ?? null;
  const unread = inbox.data?.unread ?? 0;
  // Kapsam ve tür süzgeci yüklenmiş bildirimler üzerinde; tür sayısı = o türde listelenen bildirim sayısı
  const scoped = inboxItems.filter((n) => inboxScope === 'all' || n.readAt === null);
  const catCounts: Partial<Record<NotificationCategory, number>> = {};
  for (const n of scoped) {
    const c = notificationCategory(n);
    catCounts[c] = (catCounts[c] ?? 0) + 1;
  }
  const shown = scoped.filter((n) => category === 'all' || notificationCategory(n) === category);
  const groups = groupNotifications(shown);
  const mark = useMutation({
    mutationFn: (a: { id: string; action: 'read' | 'dismiss' } | { all: true }) =>
      'all' in a ? markAllNotificationsRead() : markNotification(a.id, a.action),
    onSuccess: async () => {
      if (scope) await qc.invalidateQueries({ queryKey: qk.inbox(scope) });
    },
    onError: () => {
      toast({ tone: 'danger', title: trf.notifications.failed });
    },
  });
  useDismissable({
    open,
    refs: [triggerRef, layerRef],
    onDismiss: (reason) => {
      setOpen(false);
      setInboxPages([]);
      if (reason === 'escape') triggerRef.current?.focus();
    },
  });
  const act = useMutation({
    mutationFn: (a: { r: Reminder; action: 'complete' | 'dismiss' | 'snooze' }) =>
      a.action === 'snooze'
        ? snoozeReminder(a.r.id, {
            dueAt: new Date(Date.now() + 60 * 60 * 1000).toISOString(),
            timeZone: a.r.timeZone,
          })
        : actOnReminder(a.r.id, a.action),
    onSuccess: async (_d, a) => {
      if (scope) {
        await qc.invalidateQueries({ queryKey: qk.notifications(scope) });
        await qc.invalidateQueries({ queryKey: qk.reminders(scope, a.r.recordType, a.r.recordId) });
      }
      toast({ tone: 'success', title: trf.notifications.done });
    },
    onError: () => {
      toast({ tone: 'danger', title: trf.notifications.failed });
    },
  });

  // Açılınca odak panele taşınır (klavye/ekran okuyucu); Escape odağı düğmeye geri verir
  useEffect(() => {
    if (!open) return;
    const f = window.requestAnimationFrame(() => {
      layerRef.current?.focus();
    });
    return () => {
      window.cancelAnimationFrame(f);
    };
  }, [open]);

  const items = q.data?.data ?? [];
  const more = (q.data?.nextCursor ?? null) !== null;
  // Sayaç: tetiklenmiş hatırlatıcılar + görünür okunmamış bildirimler (sunucu en çok 100 okunmamışı sayar)
  const count = items.length + unread;
  const label =
    count > 0 ? trf.notifications.labelCount(countLabel(count, more)) : trf.notifications.label;
  // v3.9 tek seferlik vurgu: sayaç ARTINCA rozet bir kez belirir (ilk yüklemede ve azalırken yok; döngü yok)
  // Taban sayaç iki kaynak da ilk kez yüklenince alınır (ilk yüklemede vurgu olmaz)
  const ready = q.isFetched && (inbox.isFetched || scope === null);
  const [pop, setPop] = useState<{ last: number | null; key: number }>({ last: null, key: 0 });
  if (ready && pop.last !== count)
    setPop({ last: count, key: pop.last !== null && count > pop.last ? pop.key + 1 : pop.key });

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? `${base}-notif` : undefined}
        aria-label={label}
        data-testid="notification-bell"
        onClick={() => {
          setOpen((o) => !o);
        }}
        // Tema uyarlaması: üst bar simge düğmesi yuvarlak, açık zeminde beyaz yüzey
        className="relative inline-flex h-control w-control items-center justify-center rounded-pill bg-surface text-fg-muted shadow-card transition-colors duration-fast hover:text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus motion-safe:active:scale-95"
      >
        <Bell size={18} aria-hidden="true" />
        {count > 0 ? (
          <span
            key={pop.key}
            aria-hidden="true"
            className={cn(
              'absolute -right-0.5 -top-0.5 min-w-[1.125rem] rounded-full bg-danger px-1 text-center text-xs font-semibold leading-[1.125rem] text-surface',
              pop.key > 0 && 'motion-safe:animate-badge-pop',
            )}
            data-testid="notification-count"
            data-pop={pop.key > 0 ? 'true' : undefined}
          >
            {countLabel(Math.min(count, 99), more || count > 99)}
          </span>
        ) : null}
      </button>
      <span className="sr-only" role="status" aria-live="polite">
        {count > 0 ? label : ''}
      </span>
      {open ? (
        <Portal anchorRef={triggerRef}>
          <div
            ref={layerRef}
            id={`${base}-notif`}
            role="dialog"
            aria-labelledby={`${base}-title`}
            tabIndex={-1}
            style={anchoredStyle(pos)}
            className="fixed z-popover w-[min(24rem,calc(100vw-1rem))] rounded-lg border border-border bg-surface-raised shadow-overlay"
            data-testid="notification-panel"
          >
            <div className="flex items-center justify-between border-b border-border px-3 py-2">
              <h2 id={`${base}-title`} className="text-base font-semibold text-fg">
                {trf.notifications.title}
              </h2>
              {q.isFetching || inbox.isFetching ? (
                <Spinner size="sm" label={trf.notifications.loading} />
              ) : null}
            </div>
            <section aria-labelledby={`${base}-inbox`} data-testid="inbox-section">
              <div className="flex items-center justify-between gap-2 px-3 pt-2">
                <h3 id={`${base}-inbox`} className="text-sm font-semibold text-fg-muted">
                  {trf.notifications.inboxTitle}
                </h3>
                {unread > 0 ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={mark.isPending}
                    onClick={() => {
                      mark.mutate({ all: true });
                    }}
                    data-testid="inbox-read-all"
                  >
                    {trf.notifications.markAllRead}
                  </Button>
                ) : null}
              </div>
              <div className="flex flex-wrap items-center gap-2 px-3 pt-1.5">
                <div
                  role="group"
                  aria-label={trf.notifications.scopeLabel}
                  className="inline-flex rounded-md border border-border p-0.5"
                >
                  {(['unread', 'all'] as const).map((sc) => (
                    <button
                      key={sc}
                      type="button"
                      aria-pressed={inboxScope === sc}
                      onClick={() => {
                        setInboxScope(sc);
                      }}
                      data-testid={`inbox-scope-${sc}`}
                      className={cn(
                        'min-h-control-sm rounded px-2.5 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus',
                        inboxScope === sc
                          ? 'bg-selected font-semibold text-fg'
                          : 'text-fg-muted hover:text-fg',
                      )}
                    >
                      {sc === 'unread'
                        ? trf.notifications.scopeUnread(unread)
                        : trf.notifications.scopeAll}
                    </button>
                  ))}
                </div>
                <label className="sr-only" htmlFor={`${base}-cat`}>
                  {trf.notifications.categoryLabel}
                </label>
                <Select
                  id={`${base}-cat`}
                  compact
                  value={category}
                  onChange={(e) => {
                    setCategory(e.target.value as NotificationCategory);
                  }}
                  data-testid="inbox-category"
                  className="ml-auto w-auto"
                >
                  {NOTIFICATION_CATEGORIES.map((c) => (
                    <option key={c} value={c}>
                      {c === 'all'
                        ? (trf.notifications.categories[c] ?? c)
                        : trf.notifications.categoryCount(
                            trf.notifications.categories[c] ?? c,
                            catCounts[c] ?? 0,
                          )}
                    </option>
                  ))}
                </Select>
              </div>
              {inbox.isError ? (
                <p className="px-3 py-2 text-base text-danger">{trf.notifications.loadFailed}</p>
              ) : inbox.isPending ? (
                <p className="px-3 py-2 text-base text-fg-muted">{trf.notifications.loading}</p>
              ) : shown.length === 0 ? (
                <p className="px-3 py-2 text-base text-fg-muted" data-testid="inbox-empty">
                  {inboxItems.length === 0
                    ? trf.notifications.empty
                    : category !== 'all'
                      ? trf.notifications.emptyCategory
                      : trf.notifications.emptyUnread}
                </p>
              ) : (
                <ul
                  className="max-h-[min(24rem,55vh)] divide-y divide-border overflow-auto"
                  aria-label={trf.notifications.inboxTitle}
                >
                  {groups.map((g) => {
                    const n = g.head;
                    const all = [g.head, ...g.rest];
                    const unreadIds = all.filter((x) => x.readAt === null).map((x) => x.id);
                    const isUnread = unreadIds.length > 0;
                    const groupOpen = openGroups.includes(g.key);
                    const action = notificationAction(n);
                    const sla = n.topic === 'ticket_sla_breached';
                    return (
                      <li
                        key={g.key}
                        className="space-y-1.5 px-3 py-2.5"
                        data-testid="inbox-item"
                        data-unread={isUnread ? 'true' : 'false'}
                        data-group-size={all.length}
                      >
                        <div className="flex items-start gap-2">
                          {sla ? (
                            <AlertTriangle
                              size={16}
                              aria-hidden="true"
                              className="mt-0.5 shrink-0 text-danger"
                            />
                          ) : n.kind === 'feed_comment' ? (
                            <MessageSquare
                              size={16}
                              aria-hidden="true"
                              className="mt-0.5 shrink-0 text-primary"
                            />
                          ) : (
                            <UserPlus
                              size={16}
                              aria-hidden="true"
                              className="mt-0.5 shrink-0 text-primary"
                            />
                          )}
                          <div className="min-w-0 flex-1">
                            <p className="text-sm text-fg">{notificationText(n)}</p>
                            {n.record ? (
                              <p className="line-clamp-2 break-words text-base font-medium text-fg">
                                {n.record.title}
                              </p>
                            ) : null}
                            {(n.topic === 'automation_notice' || n.topic === 'channel_health') &&
                            n.message ? (
                              <p
                                className="line-clamp-3 break-words text-sm text-fg"
                                data-testid="inbox-message"
                              >
                                {n.message}
                              </p>
                            ) : null}
                            <p className="text-xs text-fg-subtle">
                              {formatDue(n.createdAt, localZone)}
                              {isUnread ? (
                                <span className="font-semibold text-primary">
                                  {' '}
                                  · {trf.notifications.unread}
                                </span>
                              ) : null}
                              {g.rest.length > 0 ? (
                                <span> · {trf.notifications.groupMore(g.rest.length)}</span>
                              ) : null}
                            </p>
                            {g.rest.length > 0 ? (
                              <button
                                type="button"
                                aria-expanded={groupOpen}
                                onClick={() => {
                                  setOpenGroups((cur) =>
                                    groupOpen ? cur.filter((k) => k !== g.key) : [...cur, g.key],
                                  );
                                }}
                                className="mt-1 min-h-control-sm rounded text-sm text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus"
                                data-testid="inbox-group-toggle"
                              >
                                {groupOpen
                                  ? trf.notifications.groupHide
                                  : trf.notifications.groupShow(all.length)}
                              </button>
                            ) : null}
                            {groupOpen ? (
                              <ul className="mt-1 space-y-0.5 border-l-2 border-border pl-2 text-xs text-fg-muted">
                                {g.rest.map((x) => (
                                  <li key={x.id} data-testid="inbox-group-entry">
                                    {notificationText(x)} · {formatDue(x.createdAt, localZone)}
                                    {x.readAt === null ? ` · ${trf.notifications.unread}` : ''}
                                  </li>
                                ))}
                              </ul>
                            ) : null}
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-1.5 pl-6">
                          {action ? (
                            <Link
                              to={action.href}
                              className="inline-flex min-h-control items-center rounded px-2 text-sm font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus"
                              data-testid="inbox-open"
                              onClick={() => {
                                for (const id of unreadIds) mark.mutate({ id, action: 'read' });
                                setOpen(false);
                              }}
                            >
                              {action.label}
                            </Link>
                          ) : null}
                          {isUnread ? (
                            <Button
                              size="sm"
                              variant="ghost"
                              disabled={mark.isPending}
                              onClick={() => {
                                for (const id of unreadIds) mark.mutate({ id, action: 'read' });
                              }}
                              data-testid="inbox-mark-read"
                            >
                              {unreadIds.length > 1
                                ? trf.notifications.markGroupRead
                                : trf.notifications.markRead}
                            </Button>
                          ) : null}
                          <Button
                            size="sm"
                            variant="ghost"
                            disabled={mark.isPending}
                            onClick={() => {
                              for (const x of all) mark.mutate({ id: x.id, action: 'dismiss' });
                            }}
                            data-testid="inbox-dismiss"
                          >
                            {trf.notifications.removeNotification}
                          </Button>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
              {inboxNext ? (
                <div className="px-3 pb-2">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setInboxPages((p) => [...p, inboxNext]);
                    }}
                  >
                    {trf.notifications.loadMore}
                  </Button>
                </div>
              ) : null}
            </section>
            <h3
              className="border-t border-border px-3 pt-2 text-sm font-semibold text-fg-muted"
              id={`${base}-rem`}
            >
              {trf.notifications.remindersTitle}
            </h3>
            {q.isError ? (
              <p className="px-3 py-3 text-base text-danger">{trf.notifications.loadFailed}</p>
            ) : q.isPending ? (
              <p className="px-3 py-3 text-base text-fg-muted">{trf.notifications.loading}</p>
            ) : items.length === 0 ? (
              <p className="px-3 py-3 text-base text-fg-muted">
                {trf.notifications.remindersEmpty}
              </p>
            ) : (
              <ul
                className="max-h-96 divide-y divide-border overflow-auto"
                aria-label={trf.notifications.title}
              >
                {items.map((r) => {
                  const t = enabledType(r.recordType);
                  return (
                    <li
                      key={r.id}
                      className="space-y-1.5 px-3 py-2.5"
                      data-testid="notification-item"
                    >
                      <div className="flex items-start gap-2">
                        <AlarmClock
                          size={16}
                          aria-hidden="true"
                          className="mt-0.5 shrink-0 text-warning"
                        />
                        <div className="min-w-0 flex-1">
                          <p className="text-sm text-fg-muted">{trf.notifications.reminderDue}</p>
                          <p className="line-clamp-2 break-words text-base font-medium text-fg">
                            {r.title}
                          </p>
                          <p className="text-xs text-fg-subtle">{formatDue(r.dueAt, r.timeZone)}</p>
                        </div>
                      </div>
                      <div className={cn('flex flex-wrap gap-1.5 pl-6')}>
                        {t ? (
                          <Link
                            to={recordHref(t, r.recordId)}
                            className="inline-flex min-h-control items-center rounded px-2 text-sm text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus"
                            onClick={() => {
                              setOpen(false);
                            }}
                          >
                            {trf.notifications.openRecord}
                          </Link>
                        ) : null}
                        <Button
                          size="sm"
                          disabled={act.isPending}
                          onClick={() => {
                            act.mutate({ r, action: 'complete' });
                          }}
                        >
                          {trf.notifications.complete}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={act.isPending}
                          onClick={() => {
                            act.mutate({ r, action: 'snooze' });
                          }}
                        >
                          {trf.notifications.snooze}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={act.isPending}
                          onClick={() => {
                            act.mutate({ r, action: 'dismiss' });
                          }}
                        >
                          {trf.notifications.dismiss}
                        </Button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
            {more ? (
              <p className="border-t border-border px-3 py-2 text-sm text-fg-muted">
                {trf.notifications.more(`${NOTIFICATION_PAGE}+`)}
              </p>
            ) : null}
            <p className="border-t border-border px-3 py-2 text-xs text-fg-subtle">
              {trf.notifications.note}
            </p>
          </div>
        </Portal>
      ) : null}
    </>
  );
}
